import React, { useEffect, useRef, useState } from 'react'
import { JackpotCardGame, type JackpotCardPickResult } from '@amusnet/react-ui'
import {
    GameUIMessageScope,
    GameUIMessageType,
    JackpotCardsGameState,
    JackpotGameCardClickedMessage,
    JackpotGameCardRevealedMessage,
    JackpotGameClickedDuringWinAnimationMessage,
    JackpotGameIntroCompletedMessage,
    JackpotGameOutroCompletedMessage,
    JackpotGamePlayIntroMessage,
    JackpotGameRevealCardsMessage,
    JackpotGameRevealCompletedMessage,
    JackpotGameSetCardsInteractionMessage,
    JackpotGameStartWinAnimationMessage,
    JackpotGameSuitLandedMessage,
    JackpotGameWinAnimationCompletedMessage,
    JackpotIndexSuitMap,
    MAX_CARDS_OF_SUIT,
    JackpotSuits,
    ViewportType,
} from 'game-ui-types'
import { useComplianceBarHeight } from '@hooks'
import { useConfigurationStore, useJackpotStore, useUltraPlayStore } from '@stores'

const getRevealedCards = (jackpotGameState: JackpotCardsGameState): Record<number, JackpotSuits> => {
    const revealedCards: Record<number, JackpotSuits> = {}

    for (const [key, value] of Object.entries(jackpotGameState)) {
        const suit = JackpotIndexSuitMap[value]

        if (suit) revealedCards[Number(key)] = suit
    }

    return revealedCards
}

const canPickRecoveredCard = (revealedCards: Record<number, JackpotSuits>): boolean => {
    const suitCounts = Object.values(revealedCards).reduce<Partial<Record<JackpotSuits, number>>>((counts, suit) => {
        counts[suit] = (counts[suit] ?? 0) + 1

        return counts
    }, {})

    return Object.values(suitCounts).every((count) => count < MAX_CARDS_OF_SUIT)
}

const JackpotCardGameWrapper: React.FC = () => {
    const jackpotStateOnMount = useJackpotStore.getState()
    const recoveredCardsOnMount = getRevealedCards(jackpotStateOnMount.jackpotGameState as JackpotCardsGameState)
    const isRecoveredRoundOnMount = jackpotStateOnMount.isJackpotRoundRunning
    const shouldUseReactJackpotGame = useConfigurationStore((state) => state.shouldUseReactJackpotGame)
    const hideCents = useConfigurationStore((state) => state.currencyHideCents)
    const winAnimationDuration = useJackpotStore((state) => state.jackpotStatsCountdownToZeroDurationSeconds)
    const isPortrait = useConfigurationStore((state) => state.viewport) === ViewportType.MOBILE_PORTRAIT
    const isUltraPlayOpen = useUltraPlayStore((state) => state.isOpen)
    const [isIntroShakeDisabled, setIsIntroShakeDisabled] = useState(false)
    const [isActive, setIsActive] = useState(isRecoveredRoundOnMount)
    const isInteractiveRef = useRef(isRecoveredRoundOnMount && canPickRecoveredCard(recoveredCardsOnMount))
    const [roundKey, setRoundKey] = useState(() => Date.now())
    const [skipIntro, setSkipIntro] = useState(isRecoveredRoundOnMount)
    const [revealedCards, setRevealedCards] = useState<Record<number, JackpotSuits>>(recoveredCardsOnMount)
    const [unpickedCards, setUnpickedCards] = useState<Record<number, JackpotSuits> | undefined>(undefined)
    const [winningSuit, setWinningSuit] = useState<JackpotSuits | undefined>(undefined)
    const [isWinCelebrationActive, setIsWinCelebrationActive] = useState(false)
    const complianceHeight = useComplianceBarHeight(isActive)

    // Map to hold pending promise resolvers for onCardPick
    const pendingPickResolversRef = useRef<Map<number, (result: JackpotCardPickResult) => void>>(new Map())

    // Stay disabled after Ultra Play closes so the skipped intro shake does not start late.
    useEffect(() => {
        if (isUltraPlayOpen) setIsIntroShakeDisabled(true)
    }, [isUltraPlayOpen])

    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            if (!useConfigurationStore.getState().shouldUseReactJackpotGame) {
                return
            }

            const rawData = event.data

            if (!rawData || typeof rawData !== 'object') {
                return
            }

            const { type, scope } = rawData

            const isJackpotScope =
                scope === GameUIMessageScope.JACKPOT_CARDS ||
                scope === GameUIMessageScope.JACKPOT ||
                scope === undefined

            if (!isJackpotScope) {
                return
            }

            switch (type) {
                case GameUIMessageType.JACKPOT_GAME_PLAY_INTRO: {
                    const data = rawData as JackpotGamePlayIntroMessage
                    const isRecovery = Boolean(data.isRecovery)

                    setIsActive(true)
                    isInteractiveRef.current = false
                    setSkipIntro(isRecovery)
                    setRoundKey(Date.now())
                    setIsIntroShakeDisabled(useUltraPlayStore.getState().isOpen)

                    if (!isRecovery) {
                        setRevealedCards({})
                    }

                    setUnpickedCards(undefined)
                    setWinningSuit(undefined)
                    setIsWinCelebrationActive(false)
                    pendingPickResolversRef.current.clear()
                    break
                }

                case GameUIMessageType.JACKPOT_GAME_SET_CARDS_INTERACTION: {
                    const data = rawData as JackpotGameSetCardsInteractionMessage
                    const interactive = Boolean(data.interactive)

                    isInteractiveRef.current = interactive
                    break
                }

                case GameUIMessageType.JACKPOT_GAME_CARD_REVEALED: {
                    const data = rawData as JackpotGameCardRevealedMessage
                    const { cardIndex, suit, winAmount: revealedWinAmount, winLevel, withAnimation } = data

                    if (revealedWinAmount !== undefined && revealedWinAmount > 0) {
                        isInteractiveRef.current = false
                    }

                    setRevealedCards((prev) => ({
                        ...prev,
                        [cardIndex]: suit,
                    }))

                    if (withAnimation === false) {
                        setSkipIntro(true)
                    }

                    const resolver = pendingPickResolversRef.current.get(cardIndex)

                    if (resolver) {
                        resolver({
                            cardIndex,
                            suit,
                            winAmount: revealedWinAmount,
                            winLevel,
                        })
                        pendingPickResolversRef.current.delete(cardIndex)
                    }

                    break
                }

                case GameUIMessageType.JACKPOT_GAME_REVEAL_CARDS: {
                    const data = rawData as JackpotGameRevealCardsMessage
                    const unpicked: Record<number, JackpotSuits> = {}

                    if (data.unpickedCards) {
                        for (const [key, value] of Object.entries(data.unpickedCards)) {
                            unpicked[Number(key)] = value
                        }
                    }

                    setUnpickedCards(unpicked)
                    setWinningSuit(data.winningSuit)
                    isInteractiveRef.current = false
                    break
                }

                case GameUIMessageType.JACKPOT_GAME_START_WIN_ANIMATION: {
                    const data = rawData as JackpotGameStartWinAnimationMessage

                    setWinningSuit(data.winSuit)
                    setIsWinCelebrationActive(true)
                    isInteractiveRef.current = false
                    break
                }

                case GameUIMessageType.JACKPOT_GAME_WAIT_FOR_AUTO_COLLECT: {
                    setIsWinCelebrationActive(true)
                    isInteractiveRef.current = false
                    break
                }

                case GameUIMessageType.JACKPOT_GAME_PLAY_OUTRO: {
                    // Outro is handled through JackpotCardGame lifecycle
                    break
                }

                case GameUIMessageType.JACKPOT_GAME_OUTRO_COMPLETED:
                case GameUIMessageType.JACKPOT_COMPLETE: {
                    setIsActive(false)
                    isInteractiveRef.current = false
                    setIsWinCelebrationActive(false)
                    pendingPickResolversRef.current.clear()
                    break
                }

                case GameUIMessageType.JACKPOT_RECOVERY: {
                    const { jackpotGameState } = rawData

                    if (jackpotGameState && typeof jackpotGameState === 'object') {
                        const recoveredCards = getRevealedCards(jackpotGameState as JackpotCardsGameState)

                        setRevealedCards(recoveredCards)
                        setRoundKey(Date.now())
                        setIsActive(true)
                        setSkipIntro(true)
                        isInteractiveRef.current = canPickRecoveredCard(recoveredCards)
                    }

                    break
                }

                default:
                    break
            }
        }

        window.uiBus.addEventListener('message', handleMessage, false)

        return () => {
            window.uiBus.removeEventListener('message', handleMessage, false)
        }
    }, [])

    const handleCardPick = (cardIndex: number): Promise<JackpotCardPickResult> => {
        if (!isInteractiveRef.current || pendingPickResolversRef.current.size > 0 || isWinCelebrationActive) {
            return Promise.reject(new Error('Interaction disabled'))
        }

        // Optimistically lock interaction while pick request is in flight
        isInteractiveRef.current = false

        const cardClickedMessage: JackpotGameCardClickedMessage = {
            type: GameUIMessageType.JACKPOT_GAME_CARD_CLICKED,
            cardIndex,
        }

        return new Promise<JackpotCardPickResult>((resolve) => {
            pendingPickResolversRef.current.set(cardIndex, resolve)
            window.uiBus.postMessage(cardClickedMessage)
        })
    }

    const handleIntroComplete = () => {
        const introCompleteMessage: JackpotGameIntroCompletedMessage = {
            type: GameUIMessageType.JACKPOT_GAME_INTRO_COMPLETED,
        }

        window.uiBus.postMessage(introCompleteMessage)
    }

    const handleSuitLanded = (suit: JackpotSuits) => {
        // Increment small suit progress in Zustand jackpot store
        useJackpotStore.setState((state) => ({
            pickedSuits: {
                ...state.pickedSuits,
                [suit]: (state.pickedSuits[suit] ?? 0) + 1,
            },
        }))

        const suitLandedMessage: JackpotGameSuitLandedMessage = {
            type: GameUIMessageType.JACKPOT_GAME_SUIT_LANDED,
            suit,
        }

        window.uiBus.postMessage(suitLandedMessage)
    }

    const handleWinAnimationStart = () => {
        const revealCompleteMessage: JackpotGameRevealCompletedMessage = {
            type: GameUIMessageType.JACKPOT_GAME_REVEAL_COMPLETED,
        }

        window.uiBus.postMessage(revealCompleteMessage)
    }

    const handleWinComplete = () => {
        const winCompleteMessage: JackpotGameWinAnimationCompletedMessage = {
            type: GameUIMessageType.JACKPOT_GAME_WIN_ANIMATION_COMPLETED,
        }

        window.uiBus.postMessage(winCompleteMessage)
    }

    const handleOutroComplete = () => {
        const outroCompleteMessage: JackpotGameOutroCompletedMessage = {
            type: GameUIMessageType.JACKPOT_GAME_OUTRO_COMPLETED,
        }

        window.uiBus.postMessage(outroCompleteMessage)
        setIsActive(false)
        isInteractiveRef.current = false
        setIsWinCelebrationActive(false)
    }

    const handleScreenClickDuringWin = () => {
        if (!isWinCelebrationActive) return

        const clickedDuringWinMessage: JackpotGameClickedDuringWinAnimationMessage = {
            type: GameUIMessageType.JACKPOT_GAME_CLICKED_DURING_WIN_ANIMATION,
        }

        window.uiBus.postMessage(clickedDuringWinMessage)
    }

    if (!shouldUseReactJackpotGame || !isActive) {
        return null
    }

    return (
        <div
            className="fixed left-0 right-0 top-0 z-[200] pointer-events-auto"
            style={{ bottom: complianceHeight }}
            onClick={isWinCelebrationActive ? handleScreenClickDuringWin : undefined}
            role="presentation"
        >
            <div className={`w-full h-full ${isWinCelebrationActive ? 'pointer-events-none' : ''}`}>
                <JackpotCardGame
                    key={roundKey}
                    className="w-full h-full"
                    disableIntroShake={isIntroShakeDisabled}
                    hideCents={hideCents}
                    initialRevealedCards={revealedCards}
                    isPortrait={isPortrait}
                    onCardPick={handleCardPick}
                    onIntroComplete={handleIntroComplete}
                    onOutroComplete={handleOutroComplete}
                    onSuitLanded={handleSuitLanded}
                    onWinAnimationStart={handleWinAnimationStart}
                    onWinComplete={handleWinComplete}
                    revealedCards={revealedCards}
                    winAnimationDuration={winAnimationDuration}
                    skipIntro={skipIntro}
                    stageClassName="w-full h-full"
                    unpickedCards={unpickedCards}
                    winningSuit={winningSuit}
                />
            </div>
            {/* The background video is composited on its own layer and can spill a device pixel past the clipped bottom
                edge. The compliance bar sits below this layer, so cover that row from inside it. */}
            <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 -bottom-[2px] h-[4px] bg-black" />
        </div>
    )
}

export default JackpotCardGameWrapper
